import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { ObjectIdLabelEntry, ObjectIdLabelMap, ObjectIdLabelService } from '../object-id-label.service';
import { ObjectIdLabelDaoService } from '../../dao/object-id-label-dao.service';
import { IMSObjectType } from 'src/app/_model/ims-object';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';



const supportedImsObjectTypes = [
  'ims/Branch',
  'ims/ServiceJob',
] as const satisfies readonly IMSObjectType[];

type SupportedImsObjectTypes =
  typeof supportedImsObjectTypes[number];

/**
 * Loads App Parameters
 */
@Injectable()
export class ObjectIdLabelLogicService
  implements ObjectIdLabelService
{
  public objectIdLabels$ = new BehaviorSubject<ObjectIdLabelMap>({
    "ims/Branch": [],
    "ims/ServiceJob": [],
  });
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private authenticationService: AuthenticationService,
    private objectIdLabelDaoService: ObjectIdLabelDaoService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    await this.objectIdLabelDaoService.reset();
  }

  public async clear() {
    this.objectIdLabelDaoService.clear();
    this.objectIdLabels$.next({});
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string, objectType: IMSObjectType): Promise<void> {
    await this.clear();
    await this.loadRemoteObjectIdLabels(apiLevel, syncKey, objectType);
  }
  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalObjectIdLabels();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async loadLocalObjectIdLabels() {
    try {
      this.logger.log(
        this.constructor.name,
        '- Load local ObjectIdLabels',
      );

      const objectIdLabelMap: ObjectIdLabelMap = {};
      for (const type of supportedImsObjectTypes) {
        const objectIdLabels: ObjectIdLabelEntry[] = await this.objectIdLabelDaoService.getObjectIdLabelsData(type);
        objectIdLabelMap[type] = objectIdLabels;
        
        this.logger.log(this.constructor.name, '- ObjectIdLabels for', type, ':', objectIdLabels?.length);
      }

      this.objectIdLabels$.next(objectIdLabelMap);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local ObjectIdLabels: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteObjectIdLabels(apiLevel: APILevel, syncKey: string, objectType: IMSObjectType) {
    if (apiLevel < APILevel.V2_OBJECTIDLABELS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load object id labels init with none',
        apiLevel,
        APILevel.V2_OBJECTIDLABELS,
      );
      await this.objectIdLabelDaoService.saveObjectIdLabelsData([], "ims/ServiceJob");
      await this.objectIdLabelDaoService.saveObjectIdLabelsData([], "ims/Branch");
      await this.loadLocalObjectIdLabels();
      return;
    }

    if (!this.authenticationService.authenticationState.getValue()) { 
    return
    }
    this.logger.log(this.constructor.name, "loadRemoteObjectIdLabels for objectType", objectType ?? supportedImsObjectTypes)
    if (objectType == undefined) {
      for (const type of supportedImsObjectTypes) {
        await this.loadRemoteObjecIdLabelsForType(apiLevel, syncKey, type);
      }
    } else {
      await this.loadRemoteObjecIdLabelsForType(apiLevel, syncKey, objectType);
    }
  }

  async initializeCompleted(): Promise<void> {}

  // Private Functions
  private async loadRemoteObjecIdLabelsForType(apiLevel: APILevel, syncKey: string, objectType: IMSObjectType) {
    this.logger.log(this.constructor.name, '- loadRemoteObjecIdLabelsForType with', objectType);

    try {
      const entries = await this.loadRemoteObjectIdLabelBatch(syncKey, objectType, []);

      this.logger.log(
        this.constructor.name,
        'Finished loading all ObjectIdLabels for',
        objectType,
        '(' + entries.length + ' Total)',
      );

      await this.objectIdLabelDaoService.saveObjectIdLabelsData(entries, objectType);
      await this.loadLocalObjectIdLabels();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading ObjectIdLabels: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteObjectIdLabelBatch(
    syncKey: string,
    objectType: IMSObjectType,
    entries: ObjectIdLabelEntry[],
    nextPart?: string,
  ): Promise<ObjectIdLabelEntry[]> {
    let data: any;
    this.logger.log(this.constructor.name, "loadRemoteObjectIdLabelBatch for", objectType, "with current", entries?.length, "entries and nextPart", nextPart)
    if (nextPart) {
      this.logger.log(this.constructor.name, "calling backend with nextPart")
      data = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey,
          },
        }),
      );
    } else {
      this.logger.log(this.constructor.name, "calling backend without nextPart")
      data = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: `v2/syncobjectidlabels/${objectType.split('/')[1]}`,
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey,
          },
        }),
      );
    }

    let nextId: string;
    const batchEntries: ObjectIdLabelEntry[] = [];
    for (const item of data) {
      if (item['#nextid']) {
        nextId = item['#nextid'];
      } else {
        batchEntries.push(item);
      }
    }

    this.logger.log(this.constructor.name, 'Downloaded Batch ObjectIdLabels for', objectType, ':', batchEntries.length,"; nextId=", nextId);

    const allEntries = entries.concat(batchEntries);
    if (nextId) {
      this.logger.log(this.constructor.name, 'Continue to download more ObjectIdLabels with nextid', nextId);
      return await this.loadRemoteObjectIdLabelBatch(syncKey, objectType, allEntries, nextId);
    }

    return allEntries;
  }
}
